import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Import AuthProvider and ProtectedRoute
import ProtectedRoute from './components/auth/PrivateRoute';
import { ProtectedModule, ProtectedRole, ProtectedSiteAdmin } from './components/auth/ProtectedAccess';
import { MonedaProvider } from './contexts/MonedaContext';
import AccessDeniedPage from './pages/AccessDeniedPage';
import UsersManagementPage from './pages/UsersManagementPage';
import AcceptInvitationPage from './pages/AcceptInvitationPage';

// Import DeviceProvider for global device connection management
import { DeviceProvider } from './contexts/DeviceContext';
import { ThemeProvider } from './contexts/ThemeContext';

// Import HIDScannerProvider for global HID scanner management
import { HIDScannerProvider } from './contexts/HIDScannerContext';

// Import SessionProvider for session code management in animal loading
import { SessionProvider } from './contexts/SessionContext';

// Import ChatProvider for AI chat functionality
import { ChatProvider } from './contexts/ChatContext';
import GlobalSpreadsheetDrop from './components/chat/GlobalSpreadsheetDrop';
import { ChatUIContextProvider } from './contexts/ChatUIContext';

// Import ARGuideProvider for AR guide functionality
import { ARGuideProvider } from './contexts/ARGuideContext';

// Import actual page components
// Páginas PÚBLICAS (eager): son las pre-renderizadas para SEO y conviene que
// monten sin fallback de Suspense, para no tapar el contenido estático del prerender.
import LandingPage from './pages/LandingPage';
import FuncionalidadesPage from './pages/FuncionalidadesPage';
import ForumPage from './pages/ForumPage';
import PreciosPage from './pages/PreciosPage';
import CalculadoraInventarioPublicaPage from './pages/CalculadoraInventarioPublicaPage';
import HistoricoPreciosPage from './pages/HistoricoPreciosPage';
import RecursosPage from './pages/RecursosPage';
import RecursoPlanillaPage from './pages/RecursoPlanillaPage';
import PlanesPage from './pages/PlanesPage';
import DeExcelAAppPage from './pages/DeExcelAAppPage';

// Import ChatWidget
import ChatWidgetContainer from './components/chat/ChatWidgetContainer';

// Import AR Guide Overlay
import { ARGuideOverlay } from './components/ar-guides/ARGuideOverlay';

// Tutorial de onboarding (KAN-232): provider puente con las guías AR +
// recordatorio en dashboard. La página hub (/tutorial) va lazy abajo.
import { TutorialProvider } from './components/tutorial/TutorialContext';

// SEO: canonical dinámico por ruta (resuelve duplicate content de Google Search Console)
import Canonical from './components/common/Canonical';

// Vercel Speed Insights
import { SpeedInsights } from '@vercel/speed-insights/react';

// Vercel Web Analytics
import { Analytics } from '@vercel/analytics/react';

// Páginas con lazy-loading (code-splitting): salen del bundle inicial y se cargan
// bajo demanda. El Service Worker las precachea (Workbox incluye todos los chunks en
// __WB_MANIFEST), así siguen disponibles offline tras el primer install.
const TutorialReminder = lazy(() => import('./components/tutorial/TutorialReminder'));
const LoginPage = lazy(() => import('./pages/Login'));
const MiOpinionPage = lazy(() => import('./pages/MiOpinionPage'));
const ResetPasswordPage = lazy(() => import('./pages/ResetPasswordPage'));
const RegistrationConfirmationPage = lazy(() => import('./pages/RegistrationConfirmationPage'));
const NoCompanyAccessPage = lazy(() => import('./pages/NoCompanyAccessPage'));
const ForumPostPage = lazy(() => import('./pages/ForumPostPage'));
const DashboardPage = lazy(() => import('./pages/DashboardPage'));
const ReportsPage = lazy(() => import('./pages/ReportsPage'));
const CattleDistributionPage = lazy(() => import('./pages/CattleDistributionPage'));
const PregnancyAnalysisPage = lazy(() => import('./pages/PregnancyAnalysisPage'));
const HealthAnalysisPage = lazy(() => import('./pages/HealthAnalysisPage'));
const ProductivityAnalysisPage = lazy(() => import('./pages/ProductivityAnalysisPage'));
const AnimalsPage = lazy(() => import('./pages/AnimalesPage'));
const AnimalPedigreePage = lazy(() => import('./pages/AnimalPedigreePage'));
const AnalisisDescendenciaPage = lazy(() => import('./pages/AnalisisDescendenciaPage'));
const UbicacionPage = lazy(() => import('./pages/UbicacionPage'));
const UbicacionDetailPage = lazy(() => import('./pages/UbicacionDetailPage'));
const UbicacionStockAnalysisPage = lazy(() => import('./pages/UbicacionStockAnalysisPage'));
const CampoStockAnalysisPage = lazy(() => import('./pages/CampoStockAnalysisPage'));
const NewUbicacionPage = lazy(() => import('./pages/NewUbicacionPage'));
const EditUbicacionPage = lazy(() => import('./pages/EditUbicacionPage'));
const NewCamposPage = lazy(() => import('./pages/NewCamposPage'));
const UnidadDetailPage = lazy(() => import('./pages/UnidadDetailPage'));
const EditUnidadPage = lazy(() => import('./pages/EditUnidadPage'));
const DevToolsPage = lazy(() => import('./pages/DevToolsPage'));
const AnimalesHistoricosPage = lazy(() => import('./pages/AnimalesHistoricosPage'));
const AnimalWeightEvolutionPage = lazy(() => import('./pages/AnimalWeightEvolutionPage'));
const AdministracionPage = lazy(() => import('./pages/AdministracionPage'));
const ConfiguracionesPage = lazy(() => import('./pages/ConfiguracionesPage'));
const InventarioPage = lazy(() => import('./pages/InventarioPage'));
const GastosPage = lazy(() => import('./pages/GastosPage'));
const IngresosPage = lazy(() => import('./pages/IngresosPage'));
const ConceptosPage = lazy(() => import('./pages/ConceptosPage'));
// LibroDiarioPage usa export con nombre (no default), por eso el .then para adaptarlo.
const LibroDiarioPage = lazy(() =>
  import('./pages/LibroDiarioPage').then((module) => ({ default: module.LibroDiarioPage }))
);
const ResultsPage = lazy(() => import('./pages/ResultsPage'));
const HojaDeRutaAdminPage = lazy(() => import('./pages/HojaDeRutaAdminPage'));
const FotosReferenciaAdminPage = lazy(() => import('./pages/FotosReferenciaAdminPage'));
const ObservatorioPage = lazy(() => import('./pages/ObservatorioPage'));
const HerramientasPage = lazy(() => import('./pages/HerramientasPage'));
const OperacionesCompraVentaPage = lazy(() => import('./pages/OperacionesCompraVentaPage'));
const SesionesCargaPage = lazy(() => import('./pages/SesionesCargaPage'));
const SesionCargaDetailPage = lazy(() => import('./pages/SesionCargaDetailPage'));
const SesionSigsaPage = lazy(() => import('./pages/SesionSigsaPage'));
const DeclaracionSigsaPage = lazy(() => import('./pages/DeclaracionSigsaPage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const ArticuloPage = lazy(() => import('./pages/ArticuloPage'));
const CalculadoraEngordePublicaPage = lazy(() => import('./pages/CalculadoraEngordePublicaPage'));
const CalculadoraRacionPublicaPage = lazy(() => import('./pages/CalculadoraRacionPublicaPage'));

// Tutorial global del puesto en el mapa (montado junto a <Routes>).
const OnboardingPuesto = lazy(() => import('./components/mapa/OnboardingPuesto'));

// Mapa del Campo y Lluvias: páginas propias del menú Principal (KAN-232).
const MapaPage = lazy(() => import('./pages/MapaPage'));
const LluviasPage = lazy(() => import('./pages/LluviasPage'));

// Hub del tutorial de onboarding (KAN-232).
const TutorialPage = lazy(() => import('./pages/TutorialPage'));

// Fallback mientras cargan los chunks lazy (rutas protegidas, tras login).
const PageLoader: React.FC = () => (
  <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 to-blue-50">
    <div className="text-gray-500 text-sm">Cargando…</div>
  </div>
);

const App: React.FC = () => {
  return (
      <ThemeProvider>
      {/* Opt-in temprano a los comportamientos v7 (silencia los future flag warnings de RRv6) */}
      <Router future={{ v7_relativeSplatPath: true, v7_startTransition: true }}>
        <Canonical />
        <ARGuideProvider>
          <TutorialProvider>
          <ChatUIContextProvider>
          <ChatProvider>
            {/* Planilla arrastrada a CUALQUIER lado de la app → importador */}
            <GlobalSpreadsheetDrop />
            <SessionProvider>
              <DeviceProvider>
                <HIDScannerProvider>
                  <Suspense fallback={<PageLoader />}>
                    <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/reset-password" element={<ResetPasswordPage />} />
          <Route path="/registro-exitoso" element={<RegistrationConfirmationPage />} />
          <Route path="/sin-empresa" element={<NoCompanyAccessPage />} />
          <Route path="/" element={<LandingPage />} />
          <Route path="/funcionalidades" element={<FuncionalidadesPage />} />
          <Route path="/foro" element={<ForumPage />} />
          <Route path="/foro/:id" element={<ForumPostPage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<ArticuloPage />} />
          <Route path="/calculadora-engorde" element={<CalculadoraEngordePublicaPage />} />
          <Route path="/calculadora-racion" element={<CalculadoraRacionPublicaPage />} />
          <Route path="/precios" element={<PreciosPage />} />
          <Route path="/calculadora-inventario" element={<CalculadoraInventarioPublicaPage />} />
          <Route path="/historico-precios" element={<HistoricoPreciosPage />} />
          <Route path="/recursos" element={<RecursosPage />} />
          <Route path="/recursos/:slug" element={<RecursoPlanillaPage />} />
          <Route path="/planes" element={<PlanesPage />} />
          <Route path="/de-excel-a-app" element={<DeExcelAAppPage />} />
          
          {/* Protected routes - require authentication */}
          <Route path="/acceso-denegado" element={<AccessDeniedPage />} />

          {/* Dashboard / statistics: cualquier autenticado */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Opinión sobre Mi Campo Digital: cualquier autenticado (todos los roles) */}
          <Route
            path="/mi-opinion"
            element={
              <ProtectedRoute>
                <MiOpinionPage />
              </ProtectedRoute>
            }
          />

          {/* Hub del tutorial de onboarding (KAN-232): cualquier autenticado */}
          <Route
            path="/tutorial"
            element={
              <ProtectedRoute>
                <TutorialPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/statistics"
            element={
              <ProtectedRoute>
                <DashboardPage />
              </ProtectedRoute>
            }
          />

          {/* Administración / finanzas — envueltas en MonedaProvider para el toggle ARS/USD */}
          <Route
            path="/administracion"
            element={
              <MonedaProvider>
                <ProtectedModule module="financial">
                  <AdministracionPage />
                </ProtectedModule>
              </MonedaProvider>
            }
          />
          <Route
            path="/administracion/stocks"
            element={
              <MonedaProvider>
                <ProtectedModule module="financial">
                  <InventarioPage />
                </ProtectedModule>
              </MonedaProvider>
            }
          />
          <Route
            path="/administracion/gastos"
            element={
              <MonedaProvider>
                <ProtectedModule module="financial">
                  <GastosPage />
                </ProtectedModule>
              </MonedaProvider>
            }
          />
          <Route
            path="/administracion/ingresos"
            element={
              <MonedaProvider>
                <ProtectedModule module="financial">
                  <IngresosPage />
                </ProtectedModule>
              </MonedaProvider>
            }
          />
          <Route
            path="/administracion/libro-diario"
            element={
              <MonedaProvider>
                <ProtectedModule module="financial">
                  <LibroDiarioPage />
                </ProtectedModule>
              </MonedaProvider>
            }
          />
          <Route
            path="/administracion/resultados"
            element={
              <MonedaProvider>
                <ProtectedModule module="financial">
                  <ResultsPage />
                </ProtectedModule>
              </MonedaProvider>
            }
          />
          <Route
            path="/administracion/conceptos"
            element={
              <MonedaProvider>
                <ProtectedModule module="financial">
                  <ConceptosPage />
                </ProtectedModule>
              </MonedaProvider>
            }
          />
          <Route
            path="/operaciones-compra-venta"
            element={
              <MonedaProvider>
                <ProtectedModule module="financial">
                  <OperacionesCompraVentaPage />
                </ProtectedModule>
              </MonedaProvider>
            }
          />

          {/* Animales */}
          <Route
            path="/animals/:caravana?"
            element={
              <ProtectedModule module="animals">
                <AnimalsPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/animales/:caravana?"
            element={
              <ProtectedModule module="animals">
                <AnimalsPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/animals/historical/:id?"
            element={
              <ProtectedModule module="animals_history">
                <AnimalesHistoricosPage />
              </ProtectedModule>
            }
          />
          {/* Árbol genealógico: sólo lectura, mismo módulo que Mi Ganado. Ruta
              de 3 segmentos: no compite con /animals/:caravana? (2 segmentos). */}
          <Route
            path="/animals/:caravana/pedigree"
            element={
              <ProtectedModule module="animals">
                <AnimalPedigreePage />
              </ProtectedModule>
            }
          />
          {/* Análisis Descendencia (ex genealogía general): rankings de
              progenitores por métricas de su descendencia. */}
          <Route
            path="/analisis-descendencia"
            element={
              <ProtectedModule module="animals">
                <AnalisisDescendenciaPage />
              </ProtectedModule>
            }
          />
          {/* Redirect de la ruta vieja de genealogía general */}
          <Route path="/genealogia" element={<Navigate to="/analisis-descendencia" replace />} />
          <Route
            path="/sesiones"
            element={
              <ProtectedModule module="animals">
                <SesionesCargaPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/sesiones/:sessionCode"
            element={
              <ProtectedModule module="animals">
                <SesionCargaDetailPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/sesiones/:sessionCode/sigsa"
            element={
              <ProtectedModule module="animals">
                <SesionSigsaPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/declaracion-sigsa"
            element={
              <ProtectedModule module="sigsa">
                <DeclaracionSigsaPage />
              </ProtectedModule>
            }
          />

          {/* Ubicaciones / Campos */}
          <Route
            path="/ubicaciones"
            element={
              <ProtectedModule module="locations">
                <UbicacionPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/ubicaciones/new"
            element={
              <ProtectedModule module="locations" action="create">
                <NewUbicacionPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/ubicaciones/:id/edit"
            element={
              <ProtectedModule module="locations" action="edit">
                <EditUbicacionPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/ubicaciones/:id"
            element={
              <ProtectedModule module="locations">
                <UbicacionDetailPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/ubicaciones/:id/stock-analysis"
            element={
              <ProtectedModule module="locations">
                <UbicacionStockAnalysisPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/campo/stock-analysis"
            element={
              <ProtectedModule module="locations">
                <CampoStockAnalysisPage />
              </ProtectedModule>
            }
          />
          {/* /campos ahora vive unificado con /ubicaciones → redirige ahí. */}
          <Route path="/campos" element={<Navigate to="/ubicaciones" replace />} />
          <Route
            path="/campos/new"
            element={
              <ProtectedModule module="locations" action="create">
                <NewCamposPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/campos/:id"
            element={
              <ProtectedModule module="locations">
                <UnidadDetailPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/campos/:id/edit"
            element={
              <ProtectedModule module="locations" action="edit">
                <EditUnidadPage />
              </ProtectedModule>
            }
          />

          {/* Reportes */}
          <Route
            path="/reports"
            element={
              <ProtectedModule module="reports">
                <ReportsPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/reports/distribution"
            element={
              <ProtectedModule module="reports">
                <CattleDistributionPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/reports/pregnancy"
            element={
              <ProtectedModule module="reports">
                <PregnancyAnalysisPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/reports/health"
            element={
              <ProtectedModule module="reports">
                <HealthAnalysisPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/reports/productivity"
            element={
              <ProtectedModule module="reports">
                <ProductivityAnalysisPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/reports/weight-evolution"
            element={
              <ProtectedModule module="reports">
                <AnimalWeightEvolutionPage />
              </ProtectedModule>
            }
          />

          {/* Herramientas */}
          <Route
            path="/herramientas"
            element={
              <ProtectedModule module="tools">
                <HerramientasPage />
              </ProtectedModule>
            }
          />

          {/* Mapa del Campo y Lluvias: páginas propias del menú Principal.
              El deep-link viejo (/herramientas?app=…) redirige acá. */}
          <Route
            path="/mapa"
            element={
              <ProtectedModule module="tools">
                <MapaPage />
              </ProtectedModule>
            }
          />
          <Route
            path="/lluvias"
            element={
              <ProtectedModule module="tools">
                <LluviasPage />
              </ProtectedModule>
            }
          />

          {/* Configuraciones (admin/owner) */}
          <Route
            path="/configuraciones"
            element={
              <ProtectedRole roles={['owner', 'admin']}>
                <ConfiguracionesPage />
              </ProtectedRole>
            }
          />

          {/* Admin del SITIO (profiles.role === 'admin'): foro, devtools, hoja de ruta */}
          <Route
            path="/admin/hoja-de-ruta"
            element={
              <ProtectedSiteAdmin>
                <HojaDeRutaAdminPage />
              </ProtectedSiteAdmin>
            }
          />
          <Route
            path="/admin/fotos-referencia"
            element={
              <ProtectedSiteAdmin>
                <FotosReferenciaAdminPage />
              </ProtectedSiteAdmin>
            }
          />
          <Route
            path="/admin/observatorio"
            element={
              <ProtectedSiteAdmin>
                <ObservatorioPage />
              </ProtectedSiteAdmin>
            }
          />
          <Route
            path="/devtools"
            element={
              <ProtectedSiteAdmin>
                <DevToolsPage />
              </ProtectedSiteAdmin>
            }
          />

          {/* Gestión de miembros de la empresa (rol de empresa owner/admin) */}
          <Route
            path="/admin/usuarios"
            element={
              <ProtectedRole roles={['owner', 'admin']}>
                <UsersManagementPage />
              </ProtectedRole>
            }
          />

          {/* Invitaciones — pública: se puede ver sin sesión.
              Al aceptar, si no hay sesión, redirige a login con returnTo. */}
          <Route
            path="/invitar/:token"
            element={<AcceptInvitationPage />}
          />
          {/* Variante cuando un cliente de email reescribe el link a /invitar?token= */}
          <Route
            path="/invitar"
            element={<AcceptInvitationPage />}
          />
                    </Routes>
                    {/* Tutorial global del puesto: aparece en cualquier página
                        de la app para usuarios sin puesto marcado (ver
                        OnboardingPuesto.tsx). */}
                    <Suspense fallback={null}>
                      <OnboardingPuesto />
                    </Suspense>
                  </Suspense>
                </HIDScannerProvider>
              </DeviceProvider>
            </SessionProvider>
            <ChatWidgetContainer />
            {/* AR Guide Overlay - debe estar dentro de ARGuideProvider */}
            <ARGuideOverlay />
            {/* Recordatorio del tutorial de onboarding (KAN-232): dashboard,
                con snooze y descarte permanente. Debe estar dentro de
                TutorialProvider (que a su vez está dentro de ARGuideProvider). */}
            <Suspense fallback={null}>
              <TutorialReminder />
            </Suspense>
          </ChatProvider>
          </ChatUIContextProvider>
          </TutorialProvider>
        </ARGuideProvider>
        <SpeedInsights />
        <Analytics />
      </Router>
      </ThemeProvider>
  );
};

export default App;
